import {
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import BoxDX from "../../layout/boxdx";
import GridDX from "../../layout/griddx";
import TypographyDX from "../../layout/typographydx";
import ButtonDX from "../../controls/buttondx";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { read, utils } from "xlsx";
import { useNotificationContext } from "../../../context/notificationcontext";
import LoadingButtonDX from "../../controls/loadingbuttondx";
import SelectListDX from "../../controls/selectlistdx";
import useSkuKitService from "../../../shared/services/skukitservice";
import { useAuthContext } from "../../../context/authcontext";

const REQUIRED_COLUMNS = ["name", "code", "skuName", "numberOfUnits"];
const ALLOWED_EXTENSIONS = ["csv", "xlsx", "xls"];

const SkuKitBulkForm = (props: any) => {
	const { t, i18n } = useTranslation();
	const { userData } = useAuthContext();
	const { customers, setCustomerId, customerId, open, handleClose, sx } = props;
	const { setInfo, setError } = useNotificationContext();
	const { addSkuKitInBulk } = useSkuKitService();

	const inputRef = useRef<HTMLInputElement>(null);
	const [file, setFile] = useState<null | File>(null);
	const [isSaving, setIsSaving] = useState(false);

	const isValidFile = (file: File) => {
		const name = file.name.toLowerCase();
		const ext = name.substring(name.lastIndexOf(".") + 1);
		return ALLOWED_EXTENSIONS.includes(ext);
	};

	const handleFileSelect = (selected: File) => {
		if (!isValidFile(selected)) {
			setError(t("Invalid file type. Please upload a CSV or Excel file."));
			return;
		}
		setError("");
		setFile(selected);
	};

	const handleClick = () => {
		inputRef.current?.click();
	};

	const handleDragOver = (event: React.DragEvent) => {
		event.preventDefault();
	};

	const handleDrop = (event: React.DragEvent) => {
		event.preventDefault();
		const dtFiles = event.dataTransfer.files;
		if (dtFiles && dtFiles[0]) {
			handleFileSelect(dtFiles[0]);
		}
	};

	const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
		const sel = event.target.files;
		if (sel && sel[0]) {
			handleFileSelect(sel[0]);
		}
	};

	const onSave = async () => {
		setError("");
		if (userData.userType !== "C" && !customerId) {
			setError(t("Please select a customer"));
			return;
		}

		if (!file) {
			setError(t("Please select a file"));
			return;
		}

		setIsSaving(true);

		const ab = await file.arrayBuffer();
		const wb = read(ab);
		const ws = wb.Sheets[wb.SheetNames[0]];
		const rawHeaders: string[] = utils.sheet_to_json(ws, {
			header: 1,
		})[0] as string[];
		const headerMap: Record<string, string> = {};
		rawHeaders.forEach((h) => {
			headerMap[h.trim().toLowerCase()] = h;
		});
		const missing = REQUIRED_COLUMNS.filter(
			(col) => !headerMap[col.toLowerCase()]
		);
		if (missing.length) {
			setError(t("Missing columns: {{cols}}", { cols: missing.join(", ") }));
			return;
		}
		const data: any[] = utils.sheet_to_json(ws, {
			raw: false, // <-- this keeps Excel text as strings
		});
		const normalized = data.map((row) => {
			const newRow: any = {};
			REQUIRED_COLUMNS.forEach((col) => {
				const rawKey = headerMap[col.toLowerCase()];
				let val = row[rawKey];
				if (typeof val === "string") {
					if (col === "skuName") val = val.trim();
					if (col === "code") val = val.toLowerCase();
				}
				newRow[col] = val;
			});
			return newRow;
		});
		const operation =
			userData.userType !== "C"
				? addSkuKitInBulk(normalized, customerId)
				: addSkuKitInBulk(normalized, userData.customerId);
		operation
			.then((res) => {
				if (res == "") {
					setInfo(t("SKU Kits added successfully"));
					handleClose(true);
				} else {
					setError(res);
					setFile(null);
				}
			})
			.catch((err: any) => {
				setError(err?.message || t("An error occurred while saving"));
			})
			.finally(() => {
				setFile(null);
				setIsSaving(false);
			});
	};

	return (
		<Dialog
			open={open}
			onClose={() => handleClose()}
			maxWidth="md"
			fullWidth
			disableEscapeKeyDown
			sx={{ direction: i18n.dir(), ...sx }}
		>
			<DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
				<TypographyDX variant="h5" fontWeight="bold">
					{t("SKU Kits Import")}
				</TypographyDX>
				{userData.userType !== "C" && (
					<GridDX item xs={12} md={4}>
						<SelectListDX
							label={t("Select Customer")}
							name="customerId"
							items={customers}
							value={customerId}
							onChange={(e: any) => setCustomerId(e.target.value)}
						/>
					</GridDX>
				)}
			</DialogTitle>
			<DialogContent>
				<GridDX container columnSpacing={1} rowSpacing={2} sx={{ py: 1 }}>
					<GridDX item xs={12} sx={{ textAlign: "center" }}>
						{file ? (
							<BoxDX>
								<Chip
									label={file.name}
									color="primary"
									onDelete={() => setFile(null)}
								/>
							</BoxDX>
						) : (
							<BoxDX
								onDragOver={handleDragOver}
								onDrop={handleDrop}
								component="fieldset"
								sx={{
									border: "1px dashed #c6c6c6",
									borderRadius: 1,
									width: 600,
									height: 150,
									mx: "auto",
								}}
							>
								<BoxDX
									display="flex"
									flexDirection="column"
									justifyContent="center"
									alignItems="center"
								>
									<Typography sx={{ fontSize: 18, textAlign: "center" }}>
										{t("Drag and Drop File here or")}
									</Typography>
									<ButtonDX variant="text" onClick={handleClick}>
										{t("Browse")}
									</ButtonDX>
								</BoxDX>
								<input
									ref={inputRef}
									accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
									type="file"
									style={{ display: "none" }}
									onChange={onChangeHandler}
								/>
							</BoxDX>
						)}
					</GridDX>
				</GridDX>
			</DialogContent>
			<DialogActions sx={{ justifyContent: "flex-end", p: 2 }}>
				<ButtonDX
					variant="outlined"
					onClick={() => handleClose()}
					sx={{ mr: 2 }}
				>
					{t("Cancel")}
				</ButtonDX>
				<LoadingButtonDX
					onClick={onSave}
					loading={isSaving}
					loadingPosition="end"
				>
					{t("Save")}
				</LoadingButtonDX>
			</DialogActions>
		</Dialog>
	);
};

export default SkuKitBulkForm;
